/**
 * @file navigation.component.tsx
 * @path apps/website/src/components/layout/navigation.component.tsx
 * @description Main navigation component with optimized morphing transitions and theme support.
 */

import { cn } from "@sirbastians/utils";
import { Menu } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

import { FullScreenMenu } from "./fullScreenMenu.component";
import { useNavigationFacade } from "@/hooks/useNavigationFacade.hook";
import { PATHS } from "@/config/paths.config";
import { branding, siteMetadata } from "@/config/site.config";

// ✨ NEW: Defined the props interface for the component
interface NavigationProps {
  lightMode?: boolean;
}

export const Navigation = ({ lightMode = false }: NavigationProps = {}) => {
  const { state, actions, refs, data } = useNavigationFacade();

  const handleSamePageScroll = (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetPath: string,
  ) => {
    if (state.pathname === targetPath) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /**
   * ✨ STRICT GLASS STYLES
   * Added 'overflow-hidden' so exiting text doesn't stretch the morphing container.
   */
  const sharedGlassStyles = cn(
    "border border-nav-border/10 bg-nav-bg/20 backdrop-blur-md neuro-bezel",
    "text-nav-text overflow-hidden",
  );

  return (
    <>
      <div
        ref={refs.scrollTriggerRef}
        className="absolute top-[70px] h-px w-full"
      />

      {/* ✨ UPDATED: Applied the theme class dynamically based on the lightMode prop */}
      <nav
        className={cn(
          "fixed left-0 right-2 top-2 z-50 p-4 md:top-6 md:p-6",
          lightMode ? "nav-theme-light" : "nav-theme-dark",
        )}
        aria-label="Main Navigation"
      >
        <div className="relative mx-auto flex h-10 max-w-screen-2xl items-center justify-between md:px-6">
          {/* LOGO SECTION */}
          <motion.div
            animate={{
              opacity: state.isCollapsed ? 0 : 1,
              pointerEvents: state.isCollapsed ? "none" : "auto",
            }}
            style={{ position: "relative" }}
            transition={{ duration: 0.3 }}
            className="relative"
          >
            <Link
              to={PATHS.ROOT}
              onClick={(e) => handleSamePageScroll(e, PATHS.ROOT)}
              className="relative flex items-center justify-between gap-4 group"
            >
              <div className="flex w-full items-center gap-3 overflow-hidden px-1">
                <div className="relative flex aspect-square size-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground neuro-bezel">
                  <img
                    src={branding.logoLight}
                    alt={siteMetadata.title}
                    className="size-full object-cover rounded-xl"
                  />
                </div>
                <div className="flex flex-1 flex-col justify-center space-y-0.5">
                  <span className="truncate text-xl font-bold tracking-widest leading-none text-nav-text">
                    {branding.title}
                  </span>
                  <span className="truncate text-xs font-bold uppercase leading-none tracking-[0.2em] text-nav-text-muted">
                    {branding.subtitle}
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* DESKTOP NAVIGATION PILL */}
          <AnimatePresence initial={false} mode="wait">
            {state.showFullNav && (
              <motion.div
                key="navbar"
                layoutId="nav-container"
                style={{ borderRadius: 9999 }}
                className={cn(
                  "absolute top-0 right-0 md:right-6 flex h-12 items-center justify-center p-1",
                  sharedGlassStyles,
                )}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
              >
                <motion.div
                  className="flex items-center space-x-1"
                  initial={{ opacity: 0 }}
                  // ✨ FIX: Added slight delay to entrance, short duration to exit
                  animate={{
                    opacity: 1,
                    transition: { duration: 0.2, delay: 0.1 },
                  }}
                  exit={{ opacity: 0, transition: { duration: 0.15 } }}
                >
                  {data.navLinksArray.map((item) => {
                    const isActive = state.pathname === item.path;
                    return (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={(e) => handleSamePageScroll(e, item.path)}
                        className={cn(
                          "cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-all duration-300 border border-transparent",
                          isActive
                            ? "underline decoration underline-offset-4 font-bold shadow-sm text-nav-text" // ✨ UPDATED: ensured active link text color respects the theme
                            : "text-nav-text-muted hover:text-nav-text",
                        )}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                </motion.div>
              </motion.div>
            )}

            {/* COLLAPSED TOGGLE / MOBILE TRIGGER */}
            {state.showToggle && (
              <motion.div
                key="menu-toggle"
                layoutId="nav-container"
                role="button"
                tabIndex={0}
                onClick={() => actions.setIsOpen(true)}
                onKeyDown={(e) => e.key === "Enter" && actions.setIsOpen(true)}
                style={{ borderRadius: 9999 }}
                className={cn(
                  "absolute top-0 right-0 md:right-6 flex h-12 w-12 cursor-pointer items-center justify-center",
                  sharedGlassStyles,
                )}
                whileHover={{
                  scale: 1.05,
                }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                aria-label="Open Navigation Menu"
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  // ✨ FIX: Added slight delay to entrance, short duration to exit
                  animate={{
                    opacity: 1,
                    scale: 1,
                    transition: { duration: 0.2, delay: 0.1 },
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.5,
                    transition: { duration: 0.15 },
                  }}
                >
                  <Menu className="h-5 w-5 md:h-6 md:w-6 text-nav-text" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>

      <FullScreenMenu
        isOpen={state.isOpen}
        onClose={() => actions.setIsOpen(false)}
      />
    </>
  );
};
